import { createContext, useContext, useMemo, useRef, type PropsWithChildren } from 'react';

import { initPrintabilityServices } from '@accio3d/shared-ui/printability/utils/config';
import { createPrintability2dService } from '@accio3d/shared-ui/printability/services/printability2dService';
import { createPrintability3dService } from '@accio3d/shared-ui/printability/services/printability3dService';
import { createPartUploadService } from '@accio3d/shared-ui/printability/services/partUploadService';
import {
  createMaterialsService,
  type MaterialsService,
} from '@accio3d/shared-ui/printability/services/materialsService';
import { createCastorMaterialsService } from '@accio3d/shared-ui/printability/services/castorMaterialsService';
import { createCastorProjectsService } from '@accio3d/shared-ui/printability/services/castorProjectsService';

import { useAuth } from '../hooks/useAuth';
import { createAgentService, type AgentService } from './agent';
import { createApiClient, createSseClient } from './client';
import { createOrganizationsService, type OrganizationsService } from './organizations';
import { createOrdersService, type OrdersService } from './orders';
import { createPartsService, type PartsService } from './parts';
import { createQuotesService, type QuotesService } from './quotes';
import { createSupplierService, type SupplierService } from './supplier';
import { createShippingService, type ShippingService } from './shipping';
import { createUserService, type UserService } from './user';
import { createDashboardService, type DashboardService } from './dashboard';
import { createNudgesService, type NudgesService } from './nudges';
import { createPaymentsService, type PaymentsService } from './payments';
import { createCapabilitiesService, type CapabilitiesService } from './capabilities';
import { createRfqsService, type RfqsService } from './rfqs';
import { createPurchaserRfqsService, type PurchaserRfqsService } from './purchaser-rfqs';
import { createSuppliersService, type SuppliersService } from './suppliers';
import { createNavCountsService, type NavCountsService } from './nav-counts';
import {
  createPurchaserAddressesService,
  type PurchaserAddressesService,
} from './purchaser-addresses';
import { createNotificationsService, type NotificationsService } from './notifications';

interface ApiServices {
  agent: AgentService;
  organizations: OrganizationsService;
  user: UserService;
  parts: PartsService;
  quotes: QuotesService;
  supplier: SupplierService;
  shipping: ShippingService;
  dashboard: DashboardService;
  nudges: NudgesService;
  payments: PaymentsService;
  capabilities: CapabilitiesService;
  orders: OrdersService;
  rfqs: RfqsService;
  purchaserRfqs: PurchaserRfqsService;
  purchaserAddresses: PurchaserAddressesService;
  suppliers: SuppliersService;
  materials: MaterialsService;
  navCounts: NavCountsService;
  notifications: NotificationsService;
}

const ApiServicesContext = createContext<ApiServices | undefined>(undefined);

/**
 * Builds the authenticated API services once and feeds the axios client live
 * auth state (entra token + active org id) via refs, so the request
 * interceptor always reads the current values without rebuilding the client.
 */
export function ApiServicesProvider({ children }: PropsWithChildren) {
  const { acquireEntraToken, organization, logout } = useAuth();

  const orgIdRef = useRef<string | undefined>(organization?.id);
  orgIdRef.current = organization?.id;

  const acquireRef = useRef(acquireEntraToken);
  acquireRef.current = acquireEntraToken;

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const services = useMemo<ApiServices>(() => {
    const clientConfig = {
      baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
      getToken: async (forceRefresh?: boolean) => {
        try {
          return await acquireRef.current(forceRefresh);
        } catch {
          return null;
        }
      },
      getOrgId: () => orgIdRef.current,
      onUnauthorized: () => {
        void logoutRef.current();
      },
    };

    const client = createApiClient(clientConfig);
    const openStream = createSseClient(clientConfig);
    const printability2dService = createPrintability2dService(client);
    const printability3dService = createPrintability3dService(client);
    const partUploadService = createPartUploadService(client);
    const materialsService = createMaterialsService(client);

    initPrintabilityServices({
      printability2dService,
      printability3dService,
      partUploadService,
      materialsService,
      castorMaterialsService: createCastorMaterialsService(client),
      castorProjectsService: createCastorProjectsService(client),
    });

    return {
      agent: createAgentService(client, openStream),
      organizations: createOrganizationsService(client),
      user: createUserService(client),
      parts: createPartsService(client),
      quotes: createQuotesService(client),
      supplier: createSupplierService(client),
      shipping: createShippingService(client),
      dashboard: createDashboardService(client),
      nudges: createNudgesService(client),
      payments: createPaymentsService(client),
      capabilities: createCapabilitiesService(client),
      orders: createOrdersService(client),
      rfqs: createRfqsService(client),
      purchaserRfqs: createPurchaserRfqsService(client),
      purchaserAddresses: createPurchaserAddressesService(client),
      suppliers: createSuppliersService(client),
      materials: materialsService,
      navCounts: createNavCountsService(client),
      notifications: createNotificationsService(client),
    };
  }, []);

  return <ApiServicesContext.Provider value={services}>{children}</ApiServicesContext.Provider>;
}

export function useApiServices(): ApiServices {
  const ctx = useContext(ApiServicesContext);
  if (!ctx) throw new Error('useApiServices must be used within ApiServicesProvider');
  return ctx;
}
